import React, { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import { usePayrollBaseUrl } from "../../utils/roleApi";
import "./SalaryRegister.scss";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";

const SalaryBudget = () => {
  const baseUrl = usePayrollBaseUrl();
  const [payrolls, setPayrolls] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [salaryData, setSalaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Months data with emojis
  const months = [
    { number: 1, name: "January", emoji: "❄️" },
    { number: 2, name: "February", emoji: "💝" },
    { number: 3, name: "March", emoji: "🌸" },
    { number: 4, name: "April", emoji: "🌷" },
    { number: 5, name: "May", emoji: "🌺" },
    { number: 6, name: "June", emoji: "☀️" },
    { number: 7, name: "July", emoji: "🌞" },
    { number: 8, name: "August", emoji: "🌻" },
    { number: 9, name: "September", emoji: "🍂" },
    { number: 10, name: "October", emoji: "🎃" },
    { number: 11, name: "November", emoji: "🦃" },
    { number: 12, name: "December", emoji: "🎄" },
  ];

  // Fetch payroll summaries on component mount
  useEffect(() => {
    fetchPayrollSummaries();
  }, []);

  // Add this new useEffect for default selections
  useEffect(() => {
    if (payrolls.length > 0 && !selectedPayroll) {
      const firstPayroll = payrolls[0];
      setSelectedPayroll(firstPayroll);
      const currentMonth = new Date().getMonth() + 1;
      const currentMonthObj = months.find(
        (month) => month.number === currentMonth,
      );
      if (currentMonthObj) {
        setSelectedMonth(currentMonthObj);
        fetchSalaryReport(
          firstPayroll.payrollId,
          currentMonthObj.number,
          selectedYear,
        );
      }
    }
  }, [payrolls, selectedYear]);

  useEffect(() => {
    if (selectedPayroll && selectedMonth) {
      fetchSalaryReport(
        selectedPayroll.payrollId,
        selectedMonth.number,
        selectedYear,
      );
    }
  }, [selectedYear]);

  const fetchPayrollSummaries = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/comp-And-Ben/summaries`,
      );
      const result = response.data;
      console.log(result);

      if (result.status === 200) {
        setPayrolls(result.data);
      } else {
        setError(result.message || "Failed to fetch payroll summaries");
      }
    } catch (err) {
      console.error(err);
      setError("Network error occurred while fetching payroll summaries");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSalaryReport = async (payrollId, month, yearParam) => {
    setLoading(true);
    setIsSubmitting(true);
    setSalaryData([]);

    // Create payroll date from selected month and current year
    const year = yearParam || selectedYear;
    const payrollDate = `${year}-${String(month).padStart(2, "0")}-01`;

    try {
      const response = await axiosInstance.post(
        `${baseUrl}/comp-And-Ben/report/salary-budget`,
        {
          payrollId: payrollId,
          payrollDate: payrollDate,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const result = response.data;
      console.log(result);

      if (result.status === 200) {
        setSalaryData(result.data);
      } else {
        setError(result.message || "Failed to fetch salary report");
      }
    } catch (err) {
      console.error(err);
      setError("Network error occurred while fetching salary report");
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handlePayrollSelection = (payroll) => {
    setSelectedPayroll(payroll);
    setError(null);
    if (selectedMonth)
      fetchSalaryReport(payroll.payrollId, selectedMonth.number, selectedYear);
  };

  const handleMonthSelection = (month) => {
    setSelectedMonth(month);
    setError(null);
    if (selectedPayroll)
      fetchSalaryReport(selectedPayroll.payrollId, month.number, selectedYear);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Get all unique salary component names for dynamic headers (excluding totals)
  const getAllSalaryComponents = (data, type) => {
    const components = new Set();
    data.forEach((record) => {
      if (record[type] && Array.isArray(record[type])) {
        record[type].forEach((item) => {
          // FIX: Check if item and item.salaryName exist before calling toLowerCase
          if (
            item &&
            item.salaryName &&
            !item.salaryName.toLowerCase().includes("total")
          ) {
            components.add(item.salaryName);
          }
        });
      }
    });
    return Array.from(components);
  };

  const fixedComponents =
    salaryData.length > 0 ? getAllSalaryComponents(salaryData, "fixed") : [];
  const employeeComponents =
    salaryData.length > 0 ? getAllSalaryComponents(salaryData, "employee") : [];
  const employerComponents =
    salaryData.length > 0 ? getAllSalaryComponents(salaryData, "employer") : [];

  // Helper function to get component value
  const getComponentValue = (components, componentName) => {
    const component = components?.find(
      (item) => item.salaryName === componentName,
    );
    return component ? component.value : 0;
  };

  // Helper function to get total earnings (fixed components excluding totals)
  const getTotalEarnings = (record) => {
    if (!record.fixed || !Array.isArray(record.fixed)) return 0;

    // Sum all fixed components except those with 'total' in the name
    return record.fixed.reduce((sum, item) => {
      // FIX: Check if item and item.salaryName exist before calling toLowerCase
      if (
        item &&
        item.salaryName &&
        !item.salaryName.toLowerCase().includes("total")
      ) {
        return sum + (item.value || 0);
      }
      return sum;
    }, 0);
  };

  // Helper function to get total deductions
  const getTotalDeductions = (record) => {
    if (!record.employee || !Array.isArray(record.employee)) return 0;

    return record.employee.reduce((sum, item) => {
      return sum + (item.value || 0);
    }, 0);
  };

  // Helper function to get total employer contributions
  const getTotalEmployerContributions = (record) => {
    if (!record.employer || !Array.isArray(record.employer)) return 0;

    // Sum all employer components except those with 'total' in the name
    return record.employer.reduce((sum, item) => {
      // FIX: Check if item and item.salaryName exist before calling toLowerCase
      if (
        item &&
        item.salaryName &&
        !item.salaryName.toLowerCase().includes("total")
      ) {
        return sum + (item.value || 0);
      }
      return sum;
    }, 0);
  };

  const calculateTotals = () => {
    return salaryData.reduce(
      (acc, employee) => {
        // Calculate gross salary from individual fixed components
        const grossSalary = getTotalEarnings(employee);
        const employeeDeductions = getTotalDeductions(employee);
        const employerContributions = getTotalEmployerContributions(employee);

        acc.totalGross += grossSalary;
        acc.totalEmployeeContribution += employeeDeductions;
        acc.totalNetPay += employee.netPay || 0;
        acc.totalEmployerContribution += employerContributions;
        acc.totalCTC += employee.ctcAmount || 0;
        return acc;
      },
      {
        totalGross: 0,
        totalEmployeeContribution: 0,
        totalNetPay: 0,
        totalEmployerContribution: 0,
        totalCTC: 0,
      },
    );
  };

  const totals = salaryData.length > 0 ? calculateTotals() : null;

  return (
    <div className="salary-registry">
      {/* New Payroll Selection Section */}
      <div className="payroll-selection">
        <div className="payroll-selection-header">
          <h2 className="heading6">Salary Budget Report</h2>

          <div className="year-selector">
            <div className="year-arrow-controls">
              <button
                className="year-arrow left"
                aria-label="Previous year"
                type="button"
                disabled={selectedYear <= 1900}
                onClick={() => setSelectedYear((y) => Math.max(1900, y - 1))}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="icon"
                  style={{ verticalAlign: "middle" }}
                >
                  <path d="M15.7 5.7a1 1 0 0 1 0 1.4L10.42 12l5.3 4.9a1 1 0 0 1-1.42 1.4l-6-5.5a1 1 0 0 1 0-1.4l6-5.5a1 1 0 0 1 1.4 0z" />
                </svg>
              </button>
              <input
                id="year-input"
                type="number"
                min={1900}
                max={2100}
                className="year-value"
                value={selectedYear}
                onChange={(e) => {
                  let val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val > 0 && val <= 9999)
                    setSelectedYear(val);
                }}
                placeholder="YYYY"
                style={{ width: 70, textAlign: "center" }}
              />
              <button
                className="year-arrow right"
                aria-label="Next year"
                type="button"
                disabled={selectedYear >= 2100}
                onClick={() => setSelectedYear((y) => Math.min(2100, y + 1))}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="icon"
                  style={{ verticalAlign: "middle" }}
                >
                  <path d="M8.3 18.3a1 1 0 0 1 0-1.4L13.58 12 8.3 7.1a1 1 0 0 1 1.42-1.4l6 5.5a1 1 0 0 1 0 1.4l-6 5.5a1 1 0 0 1-1.4 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="payroll-buttons-grid">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="loading-skeleton"
                style={{ height: "64px" }}
              ></div>
            ))}
          </div>
        ) : (
          <div className="payroll-buttons-grid">
            {payrolls.length > 0 ? (
              payrolls.map((payroll) => (
                <button
                  key={payroll.payrollId}
                  onClick={() => handlePayrollSelection(payroll)}
                  className={`payroll-button ${
                    selectedPayroll?.payrollId === payroll.payrollId
                      ? "active"
                      : ""
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  <div className="payroll-button-content">
                    <div className="payroll-text">
                      <div className="payroll-name">
                        {payroll.payrollName || `Payroll ${payroll.payrollId}`}
                      </div>
                      <div
                        className={`payroll-type-badge ${
                          payroll.payrollSetupType?.toLowerCase() === "auto"
                            ? "auto"
                            : "manual"
                        }`}
                      >
                        <span className="badge-icon">⚙️</span>
                        {payroll.payrollSetupType}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="error">
                No payroll configurations found. Please create a payroll
                configuration first.
              </div>
            )}
          </div>
        )}

        {/* Month Selection Section */}
        {selectedPayroll && (
          <div className="month-selection">
            <div className="month-cards-grid">
              {months.map((month) => (
                <button
                  key={month.number}
                  onClick={() => handleMonthSelection(month)}
                  className={`month-card ${
                    selectedMonth?.number === month.number ? "active" : ""
                  }`}
                  disabled={isLoading || isSubmitting}
                >
                  <div className="month-emoji">{month.emoji}</div>
                  <div className="month-name">{month.name}</div>
                  <div className="month-year">{selectedYear}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="salary-registry__error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div>
          <LoadingScreen />
        </div>
      )}

      {totals && (
        <div className="salary-registry__summary">
          <div className="stats-grid">
            <div className="stat-card total-gross">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalGross)}</h3>
                <p>Total Gross Salary</p>
              </div>
            </div>
            <div className="stat-card total-deductions">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">📉</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalEmployeeContribution)}</h3>
                <p>Total Employee Deductions</p>
              </div>
            </div>
            <div className="stat-card total-net">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">💵</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalNetPay)}</h3>
                <p>Total Net Pay</p>
              </div>
            </div>
            <div className="stat-card total-employer">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">🏢</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalEmployerContribution)}</h3>
                <p>Total Employer Contribution</p>
              </div>
            </div>
            <div className="stat-card total-ctc">
              <div className="stats-card__accent" aria-hidden="true" />
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>{formatCurrency(totals.totalCTC)}</h3>
                <p>Total CTC</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {salaryData.length > 0 && (
        <>
          <div className="salary-registry__table-container">
            <table className="salary-table">
              <thead>
                <tr>
                  <th rowSpan="2">Employee Name</th>
                  <th rowSpan="2">Employee ID</th>
                  <th rowSpan="2">Period</th>
                  <th rowSpan="2">Working Days</th>
                  <th rowSpan="2">LOP</th>
                  <th
                    colSpan={fixedComponents.length + 1}
                    className="earnings-header"
                  >
                    Fixed Earnings
                  </th>
                  <th
                    colSpan={employeeComponents.length + 1}
                    className="deductions-header"
                  >
                    Deductions Employee Contribution
                  </th>
                  <th rowSpan="2" className="neutral-header">
                    Take Home - Payable
                  </th>
                  <th
                    colSpan={employerComponents.length + 1}
                    className="contributions-header"
                  >
                    Benefits & Employer Contributions
                  </th>
                  <th rowSpan="2" className="neutral-header">
                    Total CTC
                  </th>
                </tr>

                {/* Sub Header Row */}
                <tr>
                  {/* Fixed Components Sub Headers - GREEN */}
                  {fixedComponents.map((component, index) => (
                    <th key={`fixed-${index}`} className="earnings-header">
                      {component}
                    </th>
                  ))}
                  <th className="earnings-header">Total Earnings</th>

                  {/* Employee Components Sub Headers - RED */}
                  {employeeComponents.map((component, index) => (
                    <th key={`employee-${index}`} className="deductions-header">
                      {component}
                    </th>
                  ))}
                  <th className="deductions-header">Total Deductions</th>

                  {/* Employer Components Sub Headers - BLUE */}
                  {employerComponents.map((component, index) => (
                    <th
                      key={`employer-${index}`}
                      className="contributions-header"
                    >
                      {component}
                    </th>
                  ))}
                  <th className="contributions-header">Total Benefits</th>
                </tr>
              </thead>
              <tbody>
                {salaryData.map((record, index) => (
                  <tr key={index}>
                    <td>{record.accountHolderName}</td>
                    <td className="neutral-column">
                      {record.userEmployeeId || "N/A"}
                    </td>
                    <td className="neutral-column">{record.period}</td>

                    {/* FIX: Use paidDays from API for "Working Days" */}
                    <td className="neutral-column">{record.paidDays || 0}</td>

                    {/* FIX: Use lossOfPay from API for "LOP" */}
                    <td className="neutral-column">{record.lossOfPay || 0}</td>

                    {/* Fixed Components Values - GREEN */}
                    {fixedComponents.map((component, i) => (
                      <td key={`fixed-value-${i}`} className="earnings-column">
                        {formatCurrency(
                          getComponentValue(record.fixed, component),
                        )}
                      </td>
                    ))}
                    <td className="earnings-column">
                      {formatCurrency(getTotalEarnings(record))}
                    </td>

                    {/* Employee Components Values - RED */}
                    {employeeComponents.map((component, i) => (
                      <td
                        key={`employee-value-${i}`}
                        className="deductions-column"
                      >
                        {formatCurrency(
                          getComponentValue(record.employee, component),
                        )}
                      </td>
                    ))}
                    <td className="deductions-column">
                      {formatCurrency(getTotalDeductions(record))}
                    </td>

                    <td className="neutral-column">
                      {formatCurrency(record.netPay || 0)}
                    </td>

                    {/* Employer Components Values - BLUE */}
                    {employerComponents.map((component, i) => (
                      <td
                        key={`employer-value-${i}`}
                        className="contributions-column"
                      >
                        {formatCurrency(
                          getComponentValue(record.employer, component),
                        )}
                      </td>
                    ))}
                    <td className="contributions-column">
                      {formatCurrency(getTotalEmployerContributions(record))}
                    </td>

                    <td className="neutral-column">
                      {formatCurrency(record.ctcAmount || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading &&
        !error &&
        salaryData.length === 0 &&
        selectedPayroll &&
        selectedMonth && (
          <div className="salary-registry__empty">
            <div className="empty-state">
              <span className="empty-icon">📊</span>
              <h3>No Data Available</h3>
              <p>No salary data found for the selected payroll and month.</p>
            </div>
          </div>
        )}
    </div>
  );
};

export default SalaryBudget;
